/**
 * ANA-R1-15 — live per-class count recompute against the confidence slider.
 *
 * ## The bug
 * The analyze step's per-class table shows `AnalyzeReport.perClass[].count` —
 * the BE rolls that up over EVERY parsed detection, BEFORE the confidence floor
 * is applied (lyse-ai-findings-import.service.ts: the per-class loop runs over
 * all detections regardless of floor/image-match; the floor only moves
 * `totals.importable` / `belowFloor`). So moving the slider never changed the
 * column — it always showed the pre-floor totals.
 *
 * ## The fix (AC1 — no new API call, no re-analyze round-trip)
 * The wizard already holds the raw label files in memory (`Step2Selection.files`
 * → `ImportLabelFile[]`, each `content` carrying `class_id cx cy w h confidence`
 * per line). We recompute the per-class counts CLIENT-SIDE from that in-memory
 * data at the chosen threshold — zero network.
 *
 * ## Matching the BE filter EXACTLY (AC2)
 * The import service keeps a detection iff `confidence >= floor` (it DROPS when
 * `d.confidence < floor` — lyse-ai-findings-import.service.ts §"4. Confidence
 * floor"). We mirror that comparison verbatim — `>=`, on the RAW parsed
 * confidence (the BE compares the raw value; the 4-dp `round4` it applies is
 * only for storage, not for the floor test). So a detection sitting EXACTLY at
 * the threshold is counted the same way the BE counts it (kept).
 *
 * Line validity mirrors the BE `parseLabelFile`: exactly 6 whitespace-separated
 * fields, `class_id` an integer ≥ 0, all numeric fields finite, `cx/cy/w/h` and
 * `confidence` in 0..1. Malformed lines are skipped (not counted), exactly like
 * the BE — so the recomputed totals never disagree with the import result.
 */
import type { ImportLabelFile } from "@aeriosiq/types";

/** One detection's class index + raw confidence — all we need to recount. */
interface DetectionLite {
  classIndex: number;
  confidence: number;
}

/**
 * Parse one label-file body into `{ classIndex, confidence }` detections,
 * applying the SAME validity rules as the BE `parseLabelFile`. Malformed lines
 * are skipped silently here (the analyze report already surfaces them with
 * file+line — this path only needs the well-formed detections to recount).
 */
export function parseDetectionsLite(content: string): DetectionLite[] {
  const out: DetectionLite[] = [];
  const lines = content.split(/\r?\n/);
  for (const raw of lines) {
    const line = raw.trim();
    if (line.length === 0) continue;
    const fields = line.split(/\s+/);
    if (fields.length !== 6) continue;
    const classIndex = Number(fields[0]);
    if (!Number.isInteger(classIndex) || classIndex < 0) continue;
    const nums = fields.slice(1).map((f) => Number(f));
    if (nums.some((n) => !Number.isFinite(n))) continue;
    const [cx, cy, w, h, confidence] = nums as [
      number,
      number,
      number,
      number,
      number,
    ];
    if ([cx, cy, w, h].some((n) => n < 0 || n > 1)) continue;
    if (confidence < 0 || confidence > 1) continue;
    out.push({ classIndex, confidence });
  }
  return out;
}

/**
 * Recompute per-class kept counts at the given floor (0..1 fraction) from the
 * in-memory label files. A detection is counted for its class iff
 * `confidence >= floorFraction` — the exact BE keep rule.
 *
 * Returns a `classIndex -> count` map covering every class index seen at the
 * threshold. A class whose detections all fall below the floor maps to 0 (only
 * if it appeared at all); absent indices simply have no entry.
 */
export function recomputePerClassCounts(
  files: readonly ImportLabelFile[],
  floorFraction: number,
): Map<number, number> {
  const counts = new Map<number, number>();
  for (const f of files) {
    for (const d of parseDetectionsLite(f.content)) {
      // BE keep rule: keep iff confidence >= floor (drop when < floor).
      if (d.confidence < floorFraction) continue;
      counts.set(d.classIndex, (counts.get(d.classIndex) ?? 0) + 1);
    }
  }
  return counts;
}
